import React from 'react';
import { GenericWidgetProps } from '../ReportcenterWidget';
import { AssistChip, Flex, Loader, Text, Title, Tooltip } from '@upsales/components';
import formatWidgetValue from '../formatWidgetValue';
import BemClass from '@upsales/components/Utils/bemClass';
import { loadDrilldown } from '../chartHelper';
import './TotalCustomersWidget.scss';

const formatTrend = (trend: number) => {
	return trend?.toString().replace('-', '') + '%';
};

const TotalCustomersWidget = ({ data, drilldown, config }: GenericWidgetProps) => {
	const classes = new BemClass('TotalCustomersWidget');
	const progress = data.total?.progress ?? 0;
	const thisYearLostCustomers = data.total?.special?.thisYearLostCustomers ?? 0;
	const thisYearNewCustomers = data.total?.special?.thisYearNewCustomers ?? 0;
	const differenceOfExistingCustomers = data.total?.special?.differenceOfExistingCustomers ?? 0;
	const hasChip = !!data.total?.special?.trend;
	const trend = data.total?.special?.trend ?? null;
	const trendGrew = trend > 0;

	const hasNewCustomers = thisYearNewCustomers > 0;
	const hasNewChip = !!data.total?.special?.newCustomersTrend;
	const newCustomersTrend = data.total?.special?.newCustomersTrend ?? 0;
	const newCustomersTrendGrew = newCustomersTrend > 0;

	const hasLostCustomers = thisYearLostCustomers > 0;
	const hasLostChip = !!data.total?.special?.negativeTrend;
	const negativeTrend = data.total?.special?.negativeTrend ?? 0;
	const customersDecreased = negativeTrend > 0;

	const language = data.language;
	const drilldownEnabled = drilldown && data.enableDrilldown;

	const openDrilldown = (widgetPart: string, shouldOpenDrilldown: boolean) => {
		if (shouldOpenDrilldown) {
			loadDrilldown([], {
				...config,
				widgetPart
			});
		}
	};

	return data.total ? (
		<Flex className={classes.b()} direction="column" space="pll pbxl prxl" gap="u10">
			<Flex alignItems="center" space="plm">
				<Title bold space="mrm">
					<Tooltip title={language?.currentTooltip ?? ''}>{formatWidgetValue(progress, 'number')}</Tooltip>
				</Title>
				{hasChip ? (
					<AssistChip
						title={`${trendGrew ? '+' : ''}${differenceOfExistingCustomers} (${
							trendGrew ? '+' : ''
						}${trend}%)`}
						type={trendGrew ? 'success' : 'danger'}
					/>
				) : null}
			</Flex>
			<Flex gap="u4">
				<Flex
					direction="column"
					gap="u1"
					onClick={() => openDrilldown('newCustomers', drilldownEnabled && hasNewCustomers)}
					className={classes.elem('subElement').mod({ clickable: hasNewCustomers }).b()}
					space="ptm pbl prm"
				>
					<Text space="plm" bold>
						{language?.newCustomers}
					</Text>
					<Flex alignItems="center" space="plm">
						<Text
							className={classes.elem('subText').b()}
							color={hasNewCustomers ? 'success-5' : 'danger-5'}
							bold
							space="mrm"
						>
							<Tooltip title={language?.newTooltip}>
								{formatWidgetValue(thisYearNewCustomers, 'number')}
							</Tooltip>
						</Text>
						{hasNewChip ? (
							<AssistChip
								title={formatTrend(newCustomersTrend)}
								icon={newCustomersTrendGrew ? 'arrow-up-right' : 'arrow-down-right'}
								type={newCustomersTrendGrew ? 'success' : 'danger'}
							/>
						) : null}
					</Flex>
				</Flex>
				<Flex
					direction="column"
					gap="u1"
					className={classes.elem('subElement').mod({ clickable: hasLostCustomers }).b()}
					onClick={() => openDrilldown('lostCustomers', drilldownEnabled && hasLostCustomers)}
					space="ptm pbl prm"
				>
					<Text space="plm" bold>
						{language?.lostCustomers}
					</Text>
					<Flex alignItems="center" space="plm">
						<Text
							className={classes.elem('subText').b()}
							color={thisYearLostCustomers === 0 ? 'success-5' : 'danger-5'}
							bold
							space="mrm"
						>
							<Tooltip title={language?.lostTooltip ?? ''}>
								{formatWidgetValue(thisYearLostCustomers, 'number')}
							</Tooltip>
						</Text>
						{hasLostChip ? (
							<AssistChip
								title={formatTrend(negativeTrend)}
								icon={customersDecreased ? 'arrow-up-right' : 'arrow-down-right'}
								type={customersDecreased ? 'danger' : 'success'}
							/>
						) : null}
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	) : (
		<Flex justifyContent="center">
			<Loader />
		</Flex>
	);
};

export default TotalCustomersWidget;
